import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmSalaries } from '../../../utils/crmPermissions';
import { isMobileContainer } from '../../../utils/device';
import {
  LayoutDashboard, Users, Calculator, Gift, Wallet, FileText,
  CreditCard, BarChart3, Settings, IndianRupee,
} from 'lucide-react';
import CrmPayrollDashboard from './CrmPayrollDashboard';
import CrmSalaryProfiles from './CrmSalaryProfiles';
import CrmPayrollProcessing from './CrmPayrollProcessing';
import CrmIncentiveManager from './CrmIncentiveManager';
import CrmAdvanceManager from './CrmAdvanceManager';
import CrmPayslips from './CrmPayslips';
import CrmPaymentHistory from './CrmPaymentHistory';
import CrmPayrollReports from './CrmPayrollReports';
import CrmPayrollSettings from './CrmPayrollSettings';

const ALL_TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, adminOnly: false },
  { id: 'profiles', label: 'Profiles', icon: Users, adminOnly: false },
  { id: 'processing', label: 'Processing', icon: Calculator, adminOnly: true },
  { id: 'incentives', label: 'Incentives', icon: Gift, adminOnly: true },
  { id: 'advances', label: 'Advances', icon: Wallet, adminOnly: false },
  { id: 'payslips', label: 'Payslips', icon: FileText, adminOnly: false },
  { id: 'payments', label: 'Payments', icon: CreditCard, adminOnly: false },
  { id: 'reports', label: 'Reports', icon: BarChart3, adminOnly: true },
  { id: 'settings', label: 'Settings', icon: Settings, adminOnly: true },
];

export default function CrmPayroll() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = canManageCrmSalaries(user?.role_slug);
  const isMobile = isMobileContainer();
  const tabs = ALL_TABS.filter((t) => isAdmin || !t.adminOnly);
  const defaultTab = isMobile ? 'payslips' : 'dashboard';
  const [tab, setTab] = useState(defaultTab);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && tabs.some((x) => x.id === t)) {
      setTab(t);
    } else if (!t) {
      setTab(defaultTab);
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const setTabAndUrl = (id) => {
    setTab(id);
    navigate(`/crm/salaries?tab=${id}`, { replace: true });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <IndianRupee className="h-6 w-6" /> Payroll
          </h1>
          <p className="text-muted-foreground text-sm">
            Salary structures, attendance-based payroll, advances, incentives, and payslips
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b pb-1 overflow-x-auto">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTabAndUrl(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-md whitespace-nowrap ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'
              }`}
            >
              <Icon className="h-4 w-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'dashboard' && <CrmPayrollDashboard />}
      {tab === 'profiles' && <CrmSalaryProfiles />}
      {tab === 'processing' && isAdmin && <CrmPayrollProcessing />}
      {tab === 'incentives' && isAdmin && <CrmIncentiveManager />}
      {tab === 'advances' && <CrmAdvanceManager />}
      {tab === 'payslips' && <CrmPayslips />}
      {tab === 'payments' && <CrmPaymentHistory />}
      {tab === 'reports' && isAdmin && <CrmPayrollReports />}
      {tab === 'settings' && isAdmin && <CrmPayrollSettings />}
    </div>
  );
}
